import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useMutation, useReactiveVar } from '@apollo/client/react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import {
  Analytics,
  getReferrer,
  parseMarkdownLinks,
  PhoneInputMask,
  Subheader,
  useTheme,
} from 'shared-components'
import { boolean, date, object, string } from 'yup'

import {
  alertType,
  dolphinBookingIntentId,
  message,
  patientData,
  reservationDateTime,
  resetDolphinReservationCache,
  responsiblePartyData,
  store,
} from '../../cache'
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '../../constants'
import { useLocation } from '../../hooks/useLocation'
import { BOOK_DOLPHIN_APPOINTMENT } from '../../queries/bookDolphinAppointment'
import { buildPatient, buildResponsibleParty } from '../../utils/helpers'

import './style.css'

export const BookingForm = () => {
  const theme = useTheme()
  const referrer = getReferrer()
  const [isBackdropOpen, setIsBackdropOpen] = useState(false)
  const [isResponsibleParty, setResponsibleParty] = useState('self')
  const datetime = useReactiveVar(reservationDateTime)
  const patient = useReactiveVar(patientData)
  const responsibleParty = useReactiveVar(responsiblePartyData)
  const { locationId, partnerId, source, attributionData } =
    useReactiveVar(store)
  const [submitAppointment, { loading }] = useMutation(BOOK_DOLPHIN_APPOINTMENT)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    insuranceRequired,
    commentRequired,
    customTermsAddendum,
    patientSexOptions,
    isPatientSexRequired,
  } = useLocation()
  const termsOfUseLabel = { inputProps: { 'aria-label': 'Terms of Use' } }
  const patientSexLabelMap = {
    FEMALE: t('Female'),
    MALE: t('Male'),
    OTHER: t('Other'),
    UNKNOWN: t('Unknown'),
    PREFER_NOT_TO_SAY: t('Prefer not to say'),
  }

  // Define min and max birthdates for dob field validation
  const minDob = new Date()
  minDob.setFullYear(minDob.getFullYear() - 120)
  const maxDob = new Date()
  maxDob.setFullYear(maxDob.getFullYear() - 1)
  const minDobFormatted = format(minDob, 'MM/dd/yyyy')
  const maxDobFormatted = format(maxDob, 'MM/dd/yyyy')
  const dobValidationMsg = t(
    'Date of birth must be between {{minDobFormatted}} and {{maxDobFormatted}}',
    {
      minDobFormatted,
      maxDobFormatted,
      interpolation: { escapeValue: false },
    }
  )

  // Define allowed characters for name field validation
  const validNameFormat = /^[\p{L}\s-]+$/u
  const nameValidationMsg = t('Name may only contain letters and hyphens')

  // Define format for email field validation
  const validEmailFormat =
    /^(?:(?:[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+(?:\.[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+)*)|(?:"(?:[\p{L}\p{N}\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\p{L}\p{N}\x01-\x09\x0b\x0c\x0e-\x7f])*"))@(?:(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+[\p{L}]{2,}|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}|IPv6:(?:[a-fA-F0-9]{1,4}(?::[a-fA-F0-9]{1,4}){7}))\])$/u
  const emailValidationMsg = t(
    'Email must be formatted as username@domain.com and cannot contain special characters in the domain'
  )

  const initialValues = () => ({
    patient: {
      firstName: patient?.firstName || '',
      lastName: patient?.lastName || '',
      email: patient?.email || '',
      phone: patient?.phone || '',
      dob: patient?.dob || '',
      sex: patient?.sex || '',
      insuranceInfo: patient?.insuranceInfo || '',
      comment: patient?.comment || '',
      termsOfUse: false,
    },
    responsibleParty: {
      firstName: responsibleParty?.firstName || '',
      lastName: responsibleParty?.lastName || '',
      email: responsibleParty?.email || '',
      phone: responsibleParty?.phone || '',
      dob: responsibleParty?.dob || '',
    },
  })

  const validationSchema = (isResponsibleParty, shouldRequireSex, sexOptions) =>
    object({
      patient: object({
        firstName: string()
          .max(250)
          .matches(validNameFormat, nameValidationMsg)
          .required(t('First name required')),
        lastName: string()
          .max(250)
          .matches(validNameFormat, nameValidationMsg)
          .required(t('Last name required')),
        dob: date()
          .max(maxDob, dobValidationMsg)
          .min(minDob, dobValidationMsg)
          .required(t('Date of birth required')),
        email: string().when([], {
          is: () => isResponsibleParty === 'self',
          then: schema =>
            schema
              .max(320)
              .matches(validEmailFormat, emailValidationMsg)
              .required(t('Email required')),
          otherwise: schema => schema.notRequired(),
        }),
        phone: string().when([], {
          is: () => isResponsibleParty === 'self',
          then: schema =>
            schema
              .test(
                'is-valid-phone',
                t('Phone number should contain 10 digits'),
                value => {
                  const digitsOnly = value ? value.replace(/\D/g, '') : ''
                  return digitsOnly.length === 10
                }
              )
              .required(t('Mobile phone required')),
          otherwise: schema => schema.notRequired(),
        }),
        sex: string().when([], {
          is: () => shouldRequireSex,
          then: schema =>
            schema
              .required(t('Sex required'))
              .oneOf(
                sexOptions,
                t('Select a valid option from the list provided')
              ),
          otherwise: schema => schema.notRequired(),
        }),
        insuranceInfo: string()
          .max(250)
          .when([], {
            is: () => insuranceRequired && !partnerId,
            then: schema => schema.required(t('Insurance name required')),
            otherwise: schema => schema.notRequired(),
          }),
        comment: string()
          .max(500)
          .when([], {
            is: () => commentRequired,
            then: schema => schema.required(t('Comment required')),
          }),
        termsOfUse: boolean().oneOf([true]).required(),
      }),
      responsibleParty: object({
        firstName: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(250)
              .matches(validNameFormat, nameValidationMsg)
              .required(t('First name required')),
          otherwise: schema => schema.notRequired(),
        }),
        lastName: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(250)
              .matches(validNameFormat, nameValidationMsg)
              .required(t('Last name required')),
          otherwise: schema => schema.notRequired(),
        }),
        dob: date().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(maxDob, dobValidationMsg)
              .min(minDob, dobValidationMsg)
              .required(t('Date of birth required')),
          otherwise: schema => schema.notRequired(),
        }),
        email: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(320)
              .matches(validEmailFormat, emailValidationMsg)
              .required(t('Email required')),
          otherwise: schema => schema.notRequired(),
        }),
        phone: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .test(
                'is-valid-phone',
                t('Phone number should contain 10 digits'),
                value => {
                  const digitsOnly = value ? value.replace(/\D/g, '') : ''
                  return digitsOnly.length === 10
                }
              )
              .required(t('Mobile phone required')),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    })

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(
      isResponsibleParty,
      isPatientSexRequired,
      patientSexOptions
    ),
    onSubmit: async values => {
      const patientValues = buildPatient(isResponsibleParty, values)
      const responsiblePartyValues = buildResponsibleParty(
        isResponsibleParty,
        values
      )
      if (partnerId && !patientValues.insuranceInfo) {
        patientValues.insuranceInfo = 'No insurance'
      }
      if (!isPatientSexRequired || !patientValues.sex) {
        delete patientValues.sex
      }
      setIsBackdropOpen(true)
      patientData(patientValues)
      responsiblePartyData(responsiblePartyValues)
      const variables = {
        datetime: format(datetime, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        locationId,
        partnerId,
        patient: patientValues,
        billingParty: responsiblePartyValues,
        referrer: referrer,
        ...(source && { source }),
        attributionData,
      }
      try {
        const { data } = await submitAppointment({ variables })
        switch (data.bookDolphinAppointment.__typename) {
          case 'ReservationUnavailable':
            await resetDolphinReservationCache()
            alertType('warning')
            message(
              t(
                'Oops! That reservation time is no longer available. Please choose a new date or time while we keep your form saved.'
              )
            )
            navigate('/')
            break
          case 'AppointmentLimitReached':
            await resetDolphinReservationCache()
            alertType('error')
            message(
              t(
                "It looks like you've booked an appointment within the last 24 hours. We recommend contacting the practice directly to make any changes. If you believe this may be a mistake, please email support@intelibly.com."
              )
            )
            navigate('/')
            break
          case 'UserInputValidationFailed': {
            alertType('error')
            const fieldErrors = data.bookDolphinAppointment.fieldErrors || []
            let emailErrorMessage = ''
            let phoneErrorMessage = ''
            const otherFieldMessages = []

            fieldErrors.forEach(fieldError => {
              const nestedErrors = fieldError.errors ?? []

              if (fieldError.field === 'patient') {
                nestedErrors.forEach(error => {
                  if (error.field === 'email') {
                    emailErrorMessage = error.message
                  } else if (error.field === 'phone') {
                    phoneErrorMessage = error.message
                  } else if (error.field === 'sex') {
                    formik.setFieldTouched('patient.sex', true, false)
                    formik.setFieldError('patient.sex', error.message)
                  } else if (error.message) {
                    otherFieldMessages.push(error.message)
                  }
                })

                if (nestedErrors.length === 0 && fieldError.message) {
                  otherFieldMessages.push(fieldError.message)
                }
              } else {
                nestedErrors.forEach(error => {
                  if (error.message) {
                    otherFieldMessages.push(error.message)
                  }
                })

                if (nestedErrors.length === 0 && fieldError.message) {
                  otherFieldMessages.push(fieldError.message)
                }
              }
            })

            if (emailErrorMessage) {
              const emailFieldPath =
                isResponsibleParty === 'other'
                  ? 'responsibleParty.email'
                  : 'patient.email'
              formik.setFieldTouched(emailFieldPath, true, false)
              formik.setFieldError(emailFieldPath, emailErrorMessage)
            }

            if (phoneErrorMessage) {
              const phoneFieldPath =
                isResponsibleParty === 'other'
                  ? 'responsibleParty.phone'
                  : 'patient.phone'
              formik.setFieldTouched(phoneFieldPath, true, false)
              formik.setFieldError(phoneFieldPath, phoneErrorMessage)
            }

            if (otherFieldMessages.length > 0) {
              message(otherFieldMessages.join(' '))
            } else if (!emailErrorMessage && !phoneErrorMessage) {
              message(t('Please review your information and try again.'))
            }
            break
          }
          default:
            if (
              data.bookDolphinAppointment.id &&
              data.bookDolphinAppointment.start
            ) {
              dolphinBookingIntentId(data.bookDolphinAppointment.id)
              navigate('/booking-confirmation')
            }
        }
      } catch (error) {
        alertType('error')
        message(
          t(
            "Oops! Something didn't quite go as planned. Give it another try later and we'll get things sorted out."
          )
        )
        navigate('/')
      } finally {
        setIsBackdropOpen(false)
      }
    },
  })

  const handleBlur = event => {
    const patientValues = buildPatient(isResponsibleParty, formik.values)
    const responsiblePartyValues = buildResponsibleParty(
      isResponsibleParty,
      formik.values
    )
    Analytics.bookingFormFieldFilled({
      fieldName: event.target.name,
      locationId,
      partnerId,
    })
    formik.handleBlur(event)
    patientData(patientValues)
    responsiblePartyData(responsiblePartyValues)
  }

  return (
    <form
      data-testid="booking-form"
      className="form"
      onSubmit={formik.handleSubmit}
    >
      <FormControl aria-label="responsible-party-section">
        <FormLabel id="responsible-party" sx={{ textAlign: 'center' }}>
          {t('Is this appointment for yourself or another person?')}
        </FormLabel>
        <RadioGroup
          aria-labelledby="Responsible party"
          name="responsible-party"
          value={isResponsibleParty}
          onChange={event => {
            setResponsibleParty(event.target.value)
          }}
          row
          sx={{ justifyContent: 'center' }}
        >
          <FormControlLabel
            value="self"
            control={<Radio />}
            label={t('Self')}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label={t('Other')}
          />
        </RadioGroup>
      </FormControl>
      {isResponsibleParty === 'other' && (
        <>
          <Subheader text={t('Responsible Party')} />
          <TextField
            id="responsible-party-first-name"
            value={formik.values.responsibleParty.firstName}
            name="responsibleParty.firstName"
            onChange={formik.handleChange}
            onBlur={handleBlur}
            label={t('Responsible Party First Name')}
            variant="outlined"
            disabled={formik.isSubmitting}
            InputProps={{
              inputProps: { maxLength: 50 },
            }}
            error={
              formik.touched.responsibleParty?.firstName &&
              Boolean(formik.errors.responsibleParty?.firstName)
            }
            helperText={
              formik.touched.responsibleParty?.firstName &&
              formik.errors.responsibleParty?.firstName
            }
            required
            fullWidth
          />
          <TextField
            id="responsible-party-last-name"
            value={formik.values.responsibleParty.lastName}
            name="responsibleParty.lastName"
            onChange={formik.handleChange}
            onBlur={handleBlur}
            error={
              formik.touched.responsibleParty?.lastName &&
              Boolean(formik.errors.responsibleParty?.lastName)
            }
            helperText={
              formik.touched.responsibleParty?.lastName &&
              formik.errors.responsibleParty?.lastName
            }
            disabled={formik.isSubmitting}
            label={t('Responsible Party Last Name')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="responsible-party-date-of-birth"
            data-testid="responsible-party-dob"
            value={formik.values.responsibleParty.dob}
            name="responsibleParty.dob"
            type="date"
            onChange={formik.handleChange}
            onBlur={handleBlur}
            label={t('Responsible Party Date of Birth')}
            variant="outlined"
            error={
              formik.touched.responsibleParty?.dob &&
              Boolean(formik.errors.responsibleParty?.dob)
            }
            helperText={
              formik.touched.responsibleParty?.dob &&
              formik.errors.responsibleParty?.dob
            }
            InputLabelProps={{ shrink: true }}
            disabled={formik.isSubmitting}
            required
            fullWidth
          />
          <TextField
            id="email"
            value={formik.values.responsibleParty.email}
            name="responsibleParty.email"
            onChange={formik.handleChange}
            onBlur={handleBlur}
            error={
              formik.touched.responsibleParty?.email &&
              Boolean(formik.errors.responsibleParty?.email)
            }
            helperText={
              formik.touched.responsibleParty?.email &&
              formik.errors.responsibleParty?.email
            }
            disabled={formik.isSubmitting}
            label={t('Responsible Party Email')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="phone"
            type="tel"
            name="responsibleParty.phone"
            variant="outlined"
            label={t('Responsible Party Mobile Phone')}
            value={formik.values.responsibleParty.phone}
            onChange={formik.handleChange}
            onBlur={handleBlur}
            error={
              formik.touched.responsibleParty?.phone &&
              Boolean(formik.errors.responsibleParty?.phone)
            }
            helperText={
              formik.touched.responsibleParty?.phone &&
              formik.errors.responsibleParty?.phone
            }
            disabled={formik.isSubmitting}
            InputProps={{ inputComponent: PhoneInputMask }}
            required
            fullWidth
          />
        </>
      )}
      <Subheader text={t('Patient Information')} />
      <TextField
        id="patient-first-name"
        value={formik.values.patient.firstName}
        name="patient.firstName"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('Patient First Name')}
        variant="outlined"
        disabled={formik.isSubmitting}
        InputProps={{
          inputProps: { maxLength: 50 },
        }}
        error={
          formik.touched.patient?.firstName &&
          Boolean(formik.errors.patient?.firstName)
        }
        helperText={
          formik.touched.patient?.firstName && formik.errors.patient?.firstName
        }
        required
        fullWidth
      />
      <TextField
        id="patient-last-name"
        value={formik.values.patient.lastName}
        name="patient.lastName"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        error={
          formik.touched.patient?.lastName &&
          Boolean(formik.errors.patient?.lastName)
        }
        helperText={
          formik.touched.patient?.lastName && formik.errors.patient?.lastName
        }
        disabled={formik.isSubmitting}
        label={t('Patient Last Name')}
        variant="outlined"
        required
        fullWidth
      />
      <TextField
        id="date"
        data-testid="patient-dob"
        value={formik.values.patient.dob}
        name="patient.dob"
        type="date"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('Patient Date of Birth')}
        variant="outlined"
        error={
          formik.touched.patient?.dob && Boolean(formik.errors.patient?.dob)
        }
        helperText={formik.touched.patient?.dob && formik.errors.patient?.dob}
        InputLabelProps={{ shrink: true }}
        disabled={formik.isSubmitting}
        required
        fullWidth
      />
      {isPatientSexRequired && (
        <TextField
          select
          id="patient-sex"
          data-testid="patient-sex"
          value={formik.values.patient.sex}
          name="patient.sex"
          onChange={formik.handleChange}
          onBlur={handleBlur}
          label={t('Sex')}
          variant="outlined"
          error={
            (formik.touched.patient?.sex || formik.submitCount > 0) &&
            Boolean(formik.errors.patient?.sex)
          }
          helperText={
            (formik.touched.patient?.sex || formik.submitCount > 0) &&
            formik.errors.patient?.sex
          }
          disabled={formik.isSubmitting}
          required
          fullWidth
        >
          <MenuItem value="">{t('Select...')}</MenuItem>
          {patientSexOptions.map(option => (
            <MenuItem key={option} value={option}>
              {patientSexLabelMap[option] ?? option}
            </MenuItem>
          ))}
        </TextField>
      )}
      {isResponsibleParty === 'self' && (
        <>
          <TextField
            id="email"
            value={formik.values.patient.email}
            name="patient.email"
            onChange={formik.handleChange}
            onBlur={handleBlur}
            error={
              formik.touched.patient?.email &&
              Boolean(formik.errors.patient?.email)
            }
            helperText={
              formik.touched.patient?.email && formik.errors.patient?.email
            }
            disabled={formik.isSubmitting}
            label={t('Patient Email')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="phone"
            type="tel"
            name="patient.phone"
            variant="outlined"
            label={t('Patient Mobile Phone')}
            value={formik.values.patient.phone}
            onChange={formik.handleChange}
            onBlur={handleBlur}
            error={
              formik.touched.patient?.phone &&
              Boolean(formik.errors.patient?.phone)
            }
            helperText={
              formik.touched.patient?.phone && formik.errors.patient?.phone
            }
            disabled={formik.isSubmitting}
            InputProps={{ inputComponent: PhoneInputMask }}
            required
            fullWidth
          />
        </>
      )}
      {!partnerId && insuranceRequired && (
        <TextField
          id="patient-insurance"
          value={formik.values.patient.insuranceInfo}
          name="patient.insuranceInfo"
          onChange={formik.handleChange}
          onBlur={handleBlur}
          error={
            formik.touched.patient?.insuranceInfo &&
            Boolean(formik.errors.patient?.insuranceInfo)
          }
          helperText={
            formik.errors.patient?.insuranceInfo
              ? formik.errors.patient?.insuranceInfo
              : t(`If you don’t have insurance, type ‘No insurance.’`)
          }
          disabled={formik.isSubmitting}
          label={t('Insurance Name')}
          variant="outlined"
          required
          fullWidth
        />
      )}
      <TextField
        id="comment"
        value={formik.values.patient.comment}
        name="patient.comment"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('Comment')}
        variant="outlined"
        minRows={2}
        maxRows={4}
        fullWidth
        multiline
        error={
          formik.touched.patient?.comment &&
          Boolean(formik.errors.patient?.comment)
        }
        disabled={formik.isSubmitting}
        required={commentRequired}
        helperText={
          formik.errors.patient?.comment
            ? formik.errors.patient?.comment
            : t(`Do not include any protected health information in the comment
        field. Additional details can be provided when contacted by the practice
        directly.`)
        }
        InputProps={{
          inputProps: { maxLength: 500 },
          sx: [
            {
              textarea: { marginBottom: theme.spacing(2) },
            },
          ],
          endAdornment: (
            <InputAdornment
              id="comment-length"
              disableTypography
              disablePointerEvents
              position="start"
              sx={[{ alignSelf: 'flex-end', position: 'absolute' }]}
            >
              {formik.values.patient.comment.length} {t('characters')}
            </InputAdornment>
          ),
        }}
      />
      <Grid
        container
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1.5}
      >
        <Grid
          container
          item
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          wrap="nowrap"
          gap={1}
        >
          <Checkbox
            {...termsOfUseLabel}
            name="patient.termsOfUse"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            color="primary"
            required
            sx={{ pt: 0.25 }}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="caption" color="rgba(0, 0, 0, 0.6)">
              I have read and accept Intelibly's{' '}
              <Link
                data-testid="form-terms-and-conditions"
                variant="caption"
                href={TERMS_AND_CONDITIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </Link>{' '}
              and I consent to Intelibly collecting data, including sensitive
              information as fully described in the{' '}
              <Link
                data-testid="form-privacy-policy"
                variant="caption"
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </Typography>
            {customTermsAddendum && (
              <Typography
                variant="caption"
                color="rgba(0, 0, 0, 0.6)"
                component="div"
                data-testid="custom-terms-addendum"
              >
                {parseMarkdownLinks(customTermsAddendum)}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item>
          <Button
            aria-label="confirm"
            color="primary"
            disabled={formik.isSubmitting}
            disableElevation
            fullWidth
            type="submit"
            variant="contained"
          >
            {t('Confirm')}
          </Button>
        </Grid>
      </Grid>
      <Backdrop
        open={isBackdropOpen || loading}
        sx={{
          color: '#fff',
          opacity: 2,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </form>
  )
}
